var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { utils, TTv4 } from "@tradetrust/open-attestation";
import { TradeTrustIDVCCode } from "../../../types/error";
import { withCodedErrorHandler } from "../../../common/errorHandler";
import { CodedError } from "../../../common/error";
import { ValidIDVCVerificationStatus } from "./idvc.type";
import MyInfoVcVerifier from "myinfo-vc-verifier";
import { ValidDidVerificationStatus, verifySignature } from "../../../did/verifier";
var name = "TradeTrustIDVCIdentityProof";
var type = "ISSUER_IDENTITY";
var skip = function () { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, {
                status: "SKIPPED",
                type: type,
                name: name,
                reason: {
                    code: TradeTrustIDVCCode.SKIPPED,
                    codeString: TradeTrustIDVCCode[TradeTrustIDVCCode.SKIPPED],
                    message: "Document was not issued using IDVC method",
                },
            }];
    });
}); };
var test = function (document) {
    if (utils.isWrappedTTV4Document(document)) {
        return document.issuer.identityProof.identityProofType === TTv4.IdentityProofType.Idvc;
    }
    return false;
};
/**
 * The verification of IDVC is just a vanilla w3c verifiable
 * credential verification.
 * Instead of relying on the NDI module, you can replace this
 * with the generic way of verifying a w3c vc.
 *  */
var verifyIDVC = function (idvc) { return __awaiter(void 0, void 0, void 0, function () {
    var revokedStatus, verificationResult;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, MyInfoVcVerifier.getRevokeStatus(idvc)];
            case 1:
                revokedStatus = _a.sent();
                if (revokedStatus) {
                    throw new CodedError("the idvc in the document has been revoked", TradeTrustIDVCCode.REVOKED_IDVC, TradeTrustIDVCCode[TradeTrustIDVCCode.REVOKED_IDVC]);
                }
                return [4 /*yield*/, MyInfoVcVerifier.verify(idvc)];
            case 2:
                verificationResult = (_a.sent()).verified;
                if (!verificationResult) {
                    throw new CodedError("the idvc in the document is invalid", TradeTrustIDVCCode.INVALID_IDVC, TradeTrustIDVCCode[TradeTrustIDVCCode.INVALID_IDVC]);
                }
                return [2 /*return*/, [revokedStatus, verificationResult]];
        }
    });
}); };
/**
 * Verification of Document with IDVC identity proof is as
 * follows:
 * 1. verify that outer signature is valid
 * 2. verify that inner vanilla vc (IDVC) is valid, via
 *    generic w3c verifiable credentials verification
 *    algorithm. Refer to:
 *    https://www.w3.org/TR/vc-data-model-2.0/#validation
 * 3. verify that the binding between issuer.id of outer
 *    combined document is the same as the one attested by
 *    the IDVC (credentialSubject.id).
 *
 * For more information read up:
 * https://github.com/TradeTrust/ndi-tradex-poc/tree/master/docs
 */
var verifyV4 = function (document, options) { return __awaiter(void 0, void 0, void 0, function () {
    var merkleRoot, _a, key, signature, did, verifySignatureStatus, idvc, _b, revokedStatus, verificationResult, doBindingsMatch, verificationStatus;
    return __generator(this, function (_c) {
        switch (_c.label) {
            case 0:
                if (!utils.isSignedWrappedTTV4Document(document))
                    throw new CodedError("document is not signed", TradeTrustIDVCCode.UNSIGNED, TradeTrustIDVCCode[TradeTrustIDVCCode.UNSIGNED]);
                merkleRoot = "0x".concat(document.proof.merkleRoot);
                _a = document.proof, key = _a.key, signature = _a.signature;
                did = document.issuer.id;
                if (!did)
                    throw new CodedError("id is missing in issuer", TradeTrustIDVCCode.DID_MISSING, TradeTrustIDVCCode[TradeTrustIDVCCode.DID_MISSING]);
                if (!key) {
                    throw new CodedError("Key is not present", TradeTrustIDVCCode.MALFORMED_IDENTITY_PROOF, TradeTrustIDVCCode[TradeTrustIDVCCode.MALFORMED_IDENTITY_PROOF]);
                }
                return [4 /*yield*/, verifySignature({
                        key: key,
                        merkleRoot: merkleRoot,
                        signature: signature,
                        did: did,
                        resolver: options.resolver,
                    })];
            case 1:
                verifySignatureStatus = _c.sent();
                if (!ValidDidVerificationStatus.guard(verifySignatureStatus)) {
                    throw new CodedError(verifySignatureStatus.reason.message, TradeTrustIDVCCode.TAMPERED, TradeTrustIDVCCode[TradeTrustIDVCCode.TAMPERED]);
                }
                if (!document.issuer.identityProof.identityVC) {
                    throw new CodedError("document does not have a identity vc", TradeTrustIDVCCode.MISSING_IDVC, TradeTrustIDVCCode[TradeTrustIDVCCode.MISSING_IDVC]);
                }
                idvc = document.issuer.identityProof.identityVC.data;
                return [4 /*yield*/, verifyIDVC(idvc)];
            case 2:
                _b = _c.sent(), revokedStatus = _b[0], verificationResult = _b[1];
                if (!idvc.credentialSubject.id) {
                    throw new CodedError("document does not have a bound issuer id in the idvc", TradeTrustIDVCCode.DID_MISSING, TradeTrustIDVCCode[TradeTrustIDVCCode.DID_MISSING]);
                }
                doBindingsMatch = did === idvc.credentialSubject.id;
                if (!doBindingsMatch) {
                    throw new CodedError("bound issuer id and idvc credential subject id are different", TradeTrustIDVCCode.WRONG_BINDING, TradeTrustIDVCCode[TradeTrustIDVCCode.WRONG_BINDING]);
                }
                verificationStatus = {
                    status: verificationResult && verifySignatureStatus.verified && doBindingsMatch && !revokedStatus ? "VALID" : "INVALID",
                    didVerificationResult: verifySignatureStatus,
                    idvcVerificationResult: verificationResult,
                    idvcRevokedStatus: revokedStatus,
                    idvcBoundId: idvc.credentialSubject.id,
                    issuerId: did,
                    key: key,
                };
                if (ValidIDVCVerificationStatus.guard(verificationStatus)) {
                    return [2 /*return*/, {
                            name: name,
                            type: type,
                            data: verificationStatus,
                            status: "VALID",
                        }];
                }
                // This might not be reachable since we mostly abort if we
                // encounter any errors previously.
                return [2 /*return*/, {
                        name: name,
                        type: type,
                        data: verificationStatus,
                        status: "INVALID",
                        reason: {
                            message: "Verification failed, look at data to diagnose.",
                            code: TradeTrustIDVCCode.INVALID_IDENTITY,
                            codeString: "INVALID_IDENTITY",
                        },
                    }];
        }
    });
}); };
var verify = function (document, options) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        if (utils.isSignedWrappedTTV4Document(document))
            return [2 /*return*/, verifyV4(document, options)];
        throw new CodedError("Document does not match tt v4 format. Consider using `utils.diagnose` from open-attestation to find out more.", TradeTrustIDVCCode.UNRECOGNIZED_DOCUMENT, TradeTrustIDVCCode[TradeTrustIDVCCode.UNRECOGNIZED_DOCUMENT]);
    });
}); };
export var tradeTrustIDVCIdentityProof = {
    skip: skip,
    test: test,
    verify: withCodedErrorHandler(verify, {
        name: name,
        type: type,
        unexpectedErrorCode: TradeTrustIDVCCode.UNEXPECTED_ERROR,
        unexpectedErrorString: TradeTrustIDVCCode[TradeTrustIDVCCode.UNEXPECTED_ERROR],
    }),
};
